import { cn } from '@/lib/utils';
import { Card } from "@/components/ui/card";
import { RESPONSIVE, SPACING, CARDS } from '@/design-system';
import { Skeleton } from "@/components/ui/skeleton";

interface PublicationCardSkeletonProps {
  variant?: 'default' | 'horizontal';
}

/**
 * Skeleton for PublicationCard — mirrors its exact layout to prevent CLS.
 * Matches aspect ratios, padding tokens, avatar sizes, and gap tokens.
 */
export function PublicationCardSkeleton({ variant = 'default' }: PublicationCardSkeletonProps) {
  if (variant === 'horizontal') {
    return (
      <Card className="overflow-hidden h-full">
        <div className="flex h-full">
          {/* Featured image — matches w-24 sm:w-32 md:w-44 aspect-[4/3] */}
          <Skeleton className="w-24 sm:w-32 md:w-44 aspect-[4/3] flex-shrink-0" />
          
          {/* Content — matches CARDS.horizontalCompact + SPACING.listItemGap */}
          <div className={cn(CARDS.horizontalCompact, 'flex-1 flex flex-col', SPACING.listItemGap)}>
            {/* Title — text-lg sm:text-xl ≈ h-6 sm:h-7 */}
            <Skeleton className="h-6 sm:h-7 w-3/4" />
            
            {/* Excerpt — flex-1 fills space, 4 lines */}
            <div className={cn("flex-1", SPACING.stackXs)}>
              <Skeleton className="h-3.5 w-full" />
              <Skeleton className="h-3.5 w-full" />
              <Skeleton className="h-3.5 w-full" />
              <Skeleton className="h-3.5 w-2/3" />
            </div>
            
            {/* Bottom metadata — pinned via mt-auto */}
            <div className={cn("mt-auto", SPACING.stackSm)}>
              {/* Date */}
              <Skeleton className="h-3 w-28" />
              {/* Author row: h-5 avatar + name */}
              <div className={cn(RESPONSIVE.flexRowCenterGap2)}>
                <Skeleton className="h-5 w-5 rounded-full" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // Default variant — flex row with portrait image + content
  return (
    <Card className="overflow-hidden h-full">
      <div className="flex">
        {/* Portrait image — matches w-28 sm:w-32 md:w-40 aspect-[3/4] */}
        <Skeleton className="w-28 sm:w-32 md:w-40 aspect-[3/4] flex-shrink-0" />
        
        {/* Content — matches CARDS.standard + SPACING.listItemGap */}
        <div className={cn(CARDS.standard, 'flex-1 flex flex-col', SPACING.listItemGap)}>
          {/* Title — text-base sm:text-lg ≈ h-5 sm:h-6, 2 lines */}
          <div className={SPACING.stackXs}>
            <Skeleton className="h-5 sm:h-6 w-full" />
            <Skeleton className="h-5 sm:h-6 w-4/5" />
          </div>
          
          {/* Date + Excerpt */}
          <div>
            <Skeleton className="h-3 w-20 mb-1.5" />
            <div className={SPACING.stackXs}>
              <Skeleton className="h-3.5 w-full" />
              <Skeleton className="h-3.5 w-full" />
              <Skeleton className="h-3.5 w-2/3" />
            </div>
          </div>
          
          {/* Author row: h-6 avatar + name + action button */}
          <div className={cn(RESPONSIVE.flexRowCenterGap2)}>
            <Skeleton className="h-6 w-6 rounded-full" />
            <Skeleton className="h-3.5 w-24 flex-1" />
            <Skeleton className="h-6 w-6 rounded-md" />
          </div>
        </div>
      </div>
    </Card>
  );
}
